import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/shared/utils/utils";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { DynamicDateTimePickerProps } from "./type";
import dayjs from "@/lib/dayjs.config";
import { Icons } from "@/assets/images";

const DynamicDateTimePicker = (
  props: DynamicDateTimePickerProps,
): React.ReactElement => {
  const {
    value,
    onChange,
    format,
    disabled,
    minDate,
    maxDate,
    className,
    placeholder,
    calendar,
    timeCss,
    errors,
    disableSameDaySelection = false,
    isClearable = false,
    closeOnDateSelection = false,
  } = props;

  const hasDate =
    format.includes("yyyy") || format.includes("dd") || format.includes("MM");
  const hasTime = format.includes("HH") || format.includes("mm");
  const hasSeconds = format.includes("ss");

  const formatDate = (date: Date | undefined, fmt: string): string => {
    if (!date) {
      return "";
    }
    // Use dayjs for formatting
    return dayjs(date).format(fmt);
  };

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    value,
  );
  const [previewDate, setPreviewDate] = React.useState<Date | undefined>(
    undefined,
  );
  const [inputValue, setInputValue] = React.useState<string>(
    formatDate(value, format),
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setSelectedDate(value);
    setPreviewDate(undefined);
    setInputValue(formatDate(value, format));
  }, [format, value]);

  const isWithinBounds = (date: Date): boolean => {
    const timestamp = date.getTime();

    if (Number.isNaN(timestamp)) {
      return false;
    }

    return (
      (!minDate || timestamp >= minDate.getTime()) &&
      (!maxDate || timestamp <= maxDate.getTime())
    );
  };

  const isDateDisabled = (date: Date): boolean => {
    if (!hasTime) {
      return !isWithinBounds(date);
    }

    if (disableSameDaySelection) {
      if (minDate && date <= minDate) {
        return true;
      }
      if (maxDate && date >= maxDate) {
        return true;
      }
    }

    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return Boolean(
      (minDate && dayEnd.getTime() < minDate.getTime()) ||
      (maxDate && dayStart.getTime() > maxDate.getTime()),
    );
  };

  const clearDate = (closePopover: boolean = true): void => {
    setSelectedDate(undefined);
    setPreviewDate(undefined);
    setInputValue("");
    if (closePopover) {
      setIsOpen(false);
    }
    if (onChange) {
      onChange(undefined);
    }
  };

  const handleDateSelect = (
    date: Date | undefined,
    options?: {
      preserveSelectedTime?: boolean;
      resetEmptyTimeToZero?: boolean;
      closePopover?: boolean;
    },
  ): void => {
    if (!date) {
      setSelectedDate(undefined);
      setPreviewDate(undefined);
      setInputValue("");
      if (onChange) {
        onChange(undefined);
      }
      return;
    }

    const preserveSelectedTime = options?.preserveSelectedTime ?? true;
    const resetEmptyTimeToZero = options?.resetEmptyTimeToZero ?? true;
    const shouldClosePopover = options?.closePopover ?? true;

    let newDate = dayjs(date);

    // Preserve time if exists in current value
    if (selectedDate && hasTime && preserveSelectedTime) {
      const prev = dayjs(selectedDate);
      newDate = newDate
        .hour(prev.hour())
        .minute(prev.minute())
        .second(prev.second());
    } else if (hasTime && resetEmptyTimeToZero) {
      newDate = newDate.hour(0).minute(0).second(0);
    }

    const jsDate = newDate.toDate();
    if (!isWithinBounds(jsDate)) {
      return;
    }

    setSelectedDate(jsDate);
    setPreviewDate(undefined);
    setInputValue(formatDate(jsDate, format));
    if (onChange) {
      onChange(jsDate);
    }
    if (shouldClosePopover && (closeOnDateSelection || !hasTime)) {
      setIsOpen(false);
    }
  };

  const revertInputValue = (): void => {
    setPreviewDate(undefined);
    setInputValue(formatDate(selectedDate, format));
  };

  const getValidInputDate = (rawText: string): Date | undefined => {
    const nextValue = rawText.trim();
    if (!nextValue) {
      return undefined;
    }

    const parsedDate = dayjs(nextValue, format, true);
    if (!parsedDate.isValid()) {
      return undefined;
    }

    const jsDate = parsedDate.toDate();
    if (isDateDisabled(jsDate)) {
      return undefined;
    }

    return jsDate;
  };

  const commitInputValue = (
    rawText: string,
    options?: { closePopover?: boolean },
  ): boolean => {
    const nextValue = rawText.trim();
    const shouldClosePopover = options?.closePopover ?? false;

    if (disabled) {
      revertInputValue();
      return false;
    }

    if (!nextValue) {
      clearDate(shouldClosePopover);
      return true;
    }

    const jsDate = getValidInputDate(nextValue);
    if (!jsDate) {
      revertInputValue();
      return false;
    }

    handleDateSelect(jsDate, {
      preserveSelectedTime: false,
      resetEmptyTimeToZero: false,
      closePopover: shouldClosePopover,
    });
    return true;
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    commitInputValue(event.clipboardData.getData("text"));
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const nextValue = event.target.value;
    setInputValue(nextValue);

    const validDate = getValidInputDate(nextValue);
    if (validDate) {
      setPreviewDate(validDate);
    }
  };

  const handleInputClick = (): void => {
    if (!disabled && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const nextFocusedElement = event.relatedTarget;
    if (
      nextFocusedElement instanceof Node &&
      popoverContentRef.current?.contains(nextFocusedElement)
    ) {
      return;
    }

    commitInputValue(event.target.value);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitInputValue(event.currentTarget.value);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      revertInputValue();
    }
  };

  const handleClear = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.PointerEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    clearDate();
  };

  const handleTimeChange = (
    type: "hour" | "minute" | "second",
    val: string,
  ): void => {
    const pickerDate = previewDate ?? selectedDate;
    const newDate = pickerDate ? new Date(pickerDate) : new Date();
    const numVal = parseInt(val, 10);

    if (type === "hour") {
      if (selectedDate) {
        newDate.setHours(numVal);
      } else {
        newDate.setHours(numVal, 0, 0, 0);
      }
    }
    if (type === "minute") {
      if (selectedDate) {
        newDate.setMinutes(numVal);
      } else {
        newDate.setHours(0, numVal, 0, 0);
      }
    }
    if (type === "second") {
      if (selectedDate) {
        newDate.setSeconds(numVal);
      } else {
        newDate.setHours(0, 0, numVal, 0);
      }
    }

    if (!isWithinBounds(newDate)) {
      return;
    }

    setSelectedDate(newDate);
    setPreviewDate(undefined);
    setInputValue(formatDate(newDate, format));
    if (onChange) {
      onChange(newDate);
    }
  };

  // Generate Options for Select

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const hours = Array.from({ length: 24 }, (_unused, i) =>
    i.toString().padStart(2, "0"),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const minutes = Array.from({ length: 60 }, (_unused, i) =>
    i.toString().padStart(2, "0"),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const seconds = Array.from({ length: 60 }, (_unused, i) =>
    i.toString().padStart(2, "0"),
  );

  const inputPlaceholder = placeholder ? placeholder : "Pick a Date";
  const pickerDate = previewDate ?? selectedDate;

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverAnchor asChild>
          <div className="relative w-full">
            <input
              type="text"
              className={cn(
                "rdt-input h-10 w-full rounded-10 border border-border bg-white px-3.5 pr-10 text-left text-sm font-medium tracking-wide text-foreground outline-none transition-all duration-500 placeholder:text-placeholder focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed select-text cursor-text",
                className,
              )}
              disabled={disabled}
              value={inputValue}
              placeholder={inputPlaceholder}
              onChange={handleInputChange}
              onClick={handleInputClick}
              onBlur={handleInputBlur}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
            />
            {hasDate && calendar && !selectedDate ? (
              <CalendarIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-placeholder" />
            ) : null}
            {isClearable && selectedDate && !disabled && (
              <button
                type="button"
                className="absolute right-2 top-1/2 z-20 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-border/40 hover:text-foreground"
                onClick={handleClear}
                aria-label="Clear date"
              >
                <img src={Icons.crossIcon} alt="Clear date" />
              </button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          ref={popoverContentRef}
          className="p-0 border border-border z-300 max-xs:w-[var(--radix-popover-trigger-width)]"
          align="start"
          side="bottom"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {hasDate && (
            <Calendar
              mode="single"
              selected={pickerDate}
              onSelect={handleDateSelect}
              initialFocus
              disabled={isDateDisabled}
            />
          )}
          {hasTime && (
            <div
              className={`py-1 w-full border-t border-border flex items-center bg-white ${timeCss ? "border border-border rounded-10 gap-4 px-3.5" : "px-5"}`}
            >
              {/* Hour */}
              <div className="flex items-center gap-1 w-full">
                <span className="text-xs text-muted-foreground">Hour</span>
                <Select
                  value={pickerDate ? pickerDate.getHours().toString() : "0"}
                  onValueChange={(val) => handleTimeChange("hour", val)}
                >
                  <SelectTrigger className="w-16 px-1.5 [&>span>span]:pr-0 gap-3 [&>img]:size-2.5">
                    <SelectValue placeholder="HH" />
                  </SelectTrigger>
                  <SelectContent
                    className="h-50 min-w-[var(--radix-select-trigger-width)]"
                    position="popper"
                    align="start"
                  >
                    {hours.map((h) => (
                      <SelectItem
                        key={h}
                        value={String(parseInt(h))}
                        className="pr-0"
                      >
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Minute */}
              <div className="flex items-center gap-1 w-full">
                <span className="text-xs text-muted-foreground">Min</span>
                <Select
                  value={pickerDate ? pickerDate.getMinutes().toString() : "0"}
                  onValueChange={(val) => handleTimeChange("minute", val)}
                >
                  <SelectTrigger className="w-16 px-1.5 [&>span>span]:pr-0 gap-3 [&>img]:size-2.5">
                    <SelectValue placeholder="mm" />
                  </SelectTrigger>
                  <SelectContent
                    className="h-50 min-w-[var(--radix-select-trigger-width)]"
                    position="popper"
                  >
                    {minutes.map((m) => (
                      <SelectItem key={m} value={String(parseInt(m))}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasSeconds && (
                <>
                  <span>:</span>

                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">Sec</span>
                    <Select
                      value={
                        pickerDate ? pickerDate.getSeconds().toString() : "0"
                      }
                      onValueChange={(val) => handleTimeChange("second", val)}
                    >
                      <SelectTrigger className="w-[64px]">
                        <SelectValue placeholder="ss" />
                      </SelectTrigger>
                      <SelectContent className="h-[200px]" position="popper">
                        {seconds.map((s) => (
                          <SelectItem key={s} value={String(parseInt(s))}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
      {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
    </>
  );
};
export { DynamicDateTimePicker };
